import { IdentificationRequestError } from "../../services/identification-report";
import { t, Trans } from "@lingui/macro";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Box,
    Button,
    Grid,
    Typography
} from "@mui/material";
import {
    Loading,
    NetworkService
} from "@sinossi/mates-react-library";
import { withSnackbar, WithSnackbarProps } from "notistack";
import React from "react";
import { withRouter } from "react-router";
import { Redirect } from "react-router-dom";
import { CustomersService } from "../../services/customers.service";
import {
    Customer,
    DocumentType,
    RequiredDocumentType
} from "../models";
import "./finalize-data.css";
import {
    FinalizeDataProps,
    FinalizeDataState
} from "./models";
import RequestedDocument from "./requested-document/requested-document";

class FinalizeData extends React.Component<FinalizeDataProps & WithSnackbarProps, FinalizeDataState> {

    private active = false;
    private statusTimer?: number;
    private statusRequestPending = false;
    private recoveryController?: AbortController;
    private requestDeadline?: number;

    /** Initializes the upload queue and report status; document counts are derived from the queue. */
    constructor(props: any) {
        super(props);

        this.state = {
            uploadDocumentList: [],
            loading: true,
            customer: null,
            onError: false,
            onSuccess: false,
            privacyAcceptance: false,
            reportStatus: null
        };

    }

    /** Starts status recovery and listens for visibility changes. */
    componentDidMount() {
        this.active = true;
        document.addEventListener("visibilitychange", this.handleVisibility);
        NetworkService.removeAuthenticationToken();
        this.pollReportStatus();
    }

    /** Resets recovery and clears the upload queue when the router opens another invitation. */
    componentDidUpdate(previous: FinalizeDataProps & WithSnackbarProps) {
        if (previous.location.pathname !== this.props.location.pathname || previous.location.search !== this.props.location.search) {
            this.cancelRecovery();
            this.setState({ customer: null, reportStatus: null, loading: true, onError: false, onSuccess: false,
                uploadDocumentList: [] }, this.pollReportStatus);
        }
    }

    /** Aborts pending requests and clears timers so navigation cannot apply late responses. */
    componentWillUnmount() {
        this.active = false;
        this.cancelRecovery();
        document.removeEventListener("visibilitychange", this.handleVisibility);
    }

    /** Invalidates the current recovery attempt and its timers. */
    private cancelRecovery = () => {
        clearTimeout(this.statusTimer);
        clearTimeout(this.requestDeadline);
        this.recoveryController?.abort();
        this.recoveryController = undefined;
        this.statusRequestPending = false;
    };

    /** Pauses in hidden tabs and resumes even if the first request never completed. */
    private handleVisibility = () => {
        this.cancelRecovery();
        if (!document.hidden && !this.state.onError && !this.state.onSuccess
            && !["SENT", "UNKNOWN"].includes(this.state.reportStatus || "")) this.pollReportStatus();
    };

    /** Reads status first, reloads outstanding requirements, and retries uncertain confirmations after five seconds. */
    private pollReportStatus = async () => {
        clearTimeout(this.statusTimer);
        if (!this.active || document.hidden || this.statusRequestPending || this.state.onError || this.state.onSuccess) return;
        const controller = new AbortController();
        this.recoveryController = controller;
        this.statusRequestPending = true;
        this.requestDeadline = window.setTimeout(() => controller.abort(), 15000);
        const id = this.getCustomerIdFromUrl();
        const auth = new URL(window.location.href).searchParams.get("authString") || "";
        /** Rejects results belonging to a previous route, hidden tab, or expired request. */
        const current = () => this.active && this.recoveryController === controller && !controller.signal.aborted;
        let retry = true;
        try {
            const response = await CustomersService.getIdentificationReportStatus(id, auth, controller.signal);
            if (!current()) return;
            let customer = this.state.customer;
            if (!customer || response.status === "WAITING_DOCUMENTS") {
                customer = await CustomersService.getCustomerForRecovery(id, auth, controller.signal);
                if (!current()) return;
            }
            if (response.status === "WAITING_DOCUMENTS" && this.hasRequirements(customer)) {
                retry = false;
                this.setState({ customer, loading: false, reportStatus: null });
            } else {
                retry = !["SENT", "UNKNOWN"].includes(response.status);
                this.setState({ customer, loading: false, reportStatus: response.status,
                    onSuccess: !!customer.identificationDocumentSubmitted });
                if (response.status === "WAITING_DOCUMENTS") {
                    await CustomersService.confirmInsertedData(id, auth, controller.signal);
                    if (current()) this.setState({ reportStatus: "QUEUED" });
                }
            }
        } catch (error) {
            if (this.active && this.recoveryController === controller) {
                const permanent = error instanceof IdentificationRequestError && [401, 403, 404, 410].includes(error.status);
                retry = !permanent;
                this.setState({ loading: false, onError: permanent });
            }
        } finally {
            if (this.recoveryController === controller) {
                clearTimeout(this.requestDeadline);
                this.statusRequestPending = false;
                this.recoveryController = undefined;
                if (retry && this.active && !document.hidden) this.statusTimer = window.setTimeout(this.pollReportStatus, 5000);
            }
        }
    };

    /** Counts current customer and linked-person requirements without relying on stale upload selections. */
    private hasRequirements = (customer: Customer): boolean => {
        return !!customer.additionalInfo?.requiredDocuments?.length
            || !!customer.ownerList?.some(person => !!person.additionalInfo?.requiredDocumentList?.length);
    };

    /** Resolves the invited customer from the current URL. */
    private getCustomerIdFromUrl = (): string => {
        const params = (new URL(window.location.href)).searchParams;
        return params.get("customerId") || this.props.match.params.id || "";
    };
    /** Uploads selected supporting documents and confirms completion, including empty selections. */
    private finalizeData = () => {

        this.setState(() => ({ loading: true }));

        this.uploadDocument(this.state.uploadDocumentList?.[0], 0);
    };

    /** Uploads the signed report with independent issue and expiry dates after SES accepts the email. */
    private readonly uploadIdentificationReport = (file: File) => {

        this.setState(() => ({ loading: true }));

        let params = (new URL(window.location.href)).searchParams;
        let authCode: string = params.get("authString") || "";
        let customerId: string = this.getCustomerIdFromUrl();

        const now = new Date();
        const expiry = new Date(now);
        expiry.setFullYear(expiry.getFullYear() + 3);

        let request = {
            documentType: DocumentType.IDENTIFICATION_RESUME,
            documentName: file.name,
            referencedEntityId: customerId,
            issueDate: now,
            expiryDate: expiry,
            number: "#1",
            file: null
        };


        CustomersService.uploadDocumentExternal(authCode, customerId, file, request,
            () => {
                this.setState(() => ({ loading: false, onSuccess: true }));
            },
            (error: any) => {

                this.setState(() => ({ loading: false }));
                this.props.enqueueSnackbar(t({
                    id: "fd-error-report-upload",
                    message: "Errore nel caricamento del report di identificazione"
                }), {
                    variant: "error",
                });
            });
    };

    /** Uploads the queue in sequence, clears it on completion, and resumes status recovery; ignores navigation callbacks. */
    private uploadDocument = (document: any, index: number) => {

        if (!this.active) return;
        if (index === this.state.uploadDocumentList!.length) {
            this.setState({ reportStatus: "WAITING_DOCUMENTS", uploadDocumentList: [] }, this.pollReportStatus);
            return;
        }

        /** Serializes document dates consistently for the upload request. */
        const formatDateField = (value: any) => {
            if (value instanceof Date) {
                return value.toISOString();
            }
            return value ?? null;
        };

        let request = {
            documentType: document.documentType,
            documentName: document.name,
            referencedEntityId: (document.person === undefined || document.person === null) ? this.state.customer?.id : document.person.id,
            issueDate: formatDateField(document.issueDate),
            expiryDate: formatDateField(document.expiryDate),
            issuer: document.issuer,
            number: document.number,
            file: null
        };

        /** Continues the upload queue after the document has committed. */
        let uploadDocumentSuccessCallback = () => {
            ++index;
            this.uploadDocument(this.state.uploadDocumentList?.[index], index);
        };

        /** Keeps the form available after a failed supporting-document upload. */
        let uploadDocumentErrorCallback = (error: any) => {
            console.log(error);
            this.setState(() => ({ loading: false }));
            this.props.enqueueSnackbar(t({
                id: "fd-error-finalization",
                message: "Errore nel termine della procedura"
            }), {
                variant: "error",
            });
        };

        let params = (new URL(window.location.href)).searchParams;
        let authCode: string = params.get("authString") || "";
        let customerId: string = this.getCustomerIdFromUrl();

        NetworkService.removeAuthenticationToken();

        CustomersService.uploadDocumentExternal(authCode, customerId, document.file, request,
            uploadDocumentSuccessCallback, uploadDocumentErrorCallback);
    };

    /** Adds or replaces a selected document by requirement ID while preserving queue order. */
    private handleFileUpload = (document: any) => {
        this.setState(previous => {
            const updatedList = [...(previous.uploadDocumentList ?? [])];
            const index = updatedList.findIndex(item => item.id === document.id);
            if (index < 0) updatedList.push(document);
            else updatedList[index] = document;
            return { uploadDocumentList: updatedList };
        });
    };

    /** Removes the selected requirement from the upload queue. */
    private handleDelete = (id: any) => {
        this.setState(previous => {
            const updatedList = (previous.uploadDocumentList ?? []).filter((doc: any) => doc.id !== id);
            return { uploadDocumentList: updatedList };
        });
    };

    /** Compares the queue length with outstanding requirements without maintaining a separate counter. */
    private correctNumberUploadedDocuments = (): boolean => {

        let numberRequiredDocuments = 0;

        const customerRequiredDocs = this.state.customer?.additionalInfo?.requiredDocuments?.length ?? 0;
        const ownerRequiredDocs = this.state.customer?.ownerList?.reduce((acc, owner) => {
            return acc + (owner.additionalInfo?.requiredDocumentList?.length ?? 0);
        }, 0) ?? 0;

        numberRequiredDocuments = customerRequiredDocs + ownerRequiredDocs;

        return (this.state.uploadDocumentList?.length ?? 0) === numberRequiredDocuments && numberRequiredDocuments > 0;
    };

    /** Displays recovery status and document forms with stable requirement IDs across metadata edits. */
    render() {
        return (
            <>
                {this.state.loading && <Loading show />}
                {this.state.onError && <Redirect to={"/identification-error"} />}
                {this.state.onSuccess && <Redirect to={"/identification-success"} />}

                {this.state.reportStatus && this.state.reportStatus !== "SENT" && <Typography role="status" textAlign="center" sx={{ mt: 4 }}>
                    {this.state.reportStatus === "UNKNOWN" ? <Trans id="fd-report-unknown">Lo stato dell’invio non è disponibile. Contatta lo studio per ricevere il report.</Trans>
                        : this.state.reportStatus === "RETRYING" ? <Trans id="fd-report-retrying">L’invio del report è in ritardo. Riproveremo automaticamente; puoi riaprire questa pagina.</Trans>
                        : <Trans id="fd-report-queued">Il report è in attesa di invio via email. Puoi riaprire questa pagina per verificarne lo stato.</Trans>}
                </Typography>}

                {/* Header e messaggi */}
                {!this.state.reportStatus && <>
                    <Typography variant="h3" className={"text-center"} sx={{ fontWeight: 'bold', mt: 3 }}>
                        <Trans id="finalize-data.header">
                            Completa la procedura
                        </Trans>
                    </Typography>

                    <Typography variant="body1" className={"text-center"} sx={{ mt: 2, color: 'text.secondary' }}>
                        <Trans id="customers-onboard.msg">
                            Gentile Cliente, inserisci i documenti richiesti per completare la procedura di identificazione
                        </Trans>
                    </Typography>
                </>}

                {this.state.reportStatus === "SENT" && <Typography variant="h5" className={"text-center"} sx={{ mt: 4, mb: 4, color: 'text.primary' }}>
                    <Trans id="customers-onboard-idreport.msg">
                        Controlla la tua casella E-Mail e carica il file di riepilogo identificazione che hai ricevuto, dopo averlo firmato
                    </Trans>
                </Typography>
                }
                {!this.state.reportStatus && (
                    <Box sx={{
                        backgroundColor: "#FAFBFB",
                        p: 3,
                        mt: 3,
                        boxShadow: 4,
                        borderRadius: 2
                    }} className="finalize-data-container">
                        <Grid container>
                            <Grid item xs={12}>
                                {this.state.customer?.ownerList?.map(
                                    (item: any) => ((item.additionalInfo.requiredDocumentList).map(
                                        (document: RequiredDocumentType, index: number) => (<RequestedDocument
                                            document={document}
                                            isPerson={true}
                                            person={item}
                                            key={item.id + "-document-" + index}
                                            isFirst={index === 0}
                                            myKey={item.id + "-document-" + index}
                                            fileUploadFunction={this.handleFileUpload}
                                            removeDocument={this.handleDelete} />))))}
                                {this.state.customer?.additionalInfo?.requiredDocuments!.map(
                                    (item: RequiredDocumentType, index: number) => (<RequestedDocument
                                        document={item}
                                        isPerson={false}
                                        key={index}
                                        isFirst={index === 0}
                                        myKey={this.state.customer!.id + "-document-" + index}
                                        fileUploadFunction={this.handleFileUpload}
                                        removeDocument={this.handleDelete} />))}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 4 }} textAlign={"center"}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={!this.correctNumberUploadedDocuments()}
                                    onClick={this.finalizeData}
                                    startIcon={<CheckCircleIcon />}
                                    sx={{
                                        fontSize: '1.1rem',
                                        padding: '10px 20px',
                                        borderRadius: 3,
                                        boxShadow: 3,
                                    }}
                                >
                                    <Trans id={"fd-identification-end"}>
                                        Termina identificazione
                                    </Trans>
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {this.state.reportStatus === "SENT" && (
                    <Box sx={{
                        backgroundColor: "#FAFBFB",
                        p: 3,
                        mt: 3,
                        boxShadow: 4,
                        borderRadius: 2,
                        textAlign: 'center'
                    }} className="finalize-data-container">
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            <Trans id="fd-report-upload-title">Carica il report di identificazione firmato</Trans>
                        </Typography>
                        <input
                            type="file"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    this.uploadIdentificationReport(file);
                                }
                            }}
                            style={{ display: 'none' }}
                            id="report-file"
                        />
                        <label htmlFor="report-file">
                            <Button variant="contained" component="span" color="secondary">
                                <Trans id="fd-upload-report-btn">Scegli il file</Trans>
                            </Button>
                        </label>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <Trans id="fd-upload-report-size">Massimo 15MB</Trans>
                        </Typography>
                    </Box>
                )}
            </>
        );
    }


}

export default withSnackbar(withRouter(FinalizeData));
